<script setup>
import { ref, computed, watch, onUnmounted } from 'vue'
import { useUserStore } from '@/stores/user'
import { useGeoStore } from '@/stores/geo'
import { useLayersStore } from '@/stores/layers'
import { useRoute } from 'vue-router'
import { formatTime } from '@/utils/datetime'
import { toast } from '@/components/ui/toast'
import { Slider } from '@/components/ui/slider'
import DetailCollectedData from './DetailCollectedData.vue'
import DetailPlanetOrder from './DetailPlanetOrder.vue'
import DetailPlanetVars from './DetailPlanetVars.vue'
import DetailPlanetscope from './DetailPlanetscope.vue'
import DetailRadar from './DetailRadar.vue'
import DetailStaticLayer from './DetailStaticLayer.vue'
import DetailTasking from './DetailTasking.vue'
import DetailLulc from './DetailLulc.vue'

const route = useRoute()
const userStore = useUserStore()
const geoStore = useGeoStore()
const layersStore = useLayersStore()
const orgId = computed(() => route.params.org_id)
const layerId = computed(() => route.params.layer_id)
const opacity = ref(100)
const map = computed(() => geoStore.map)

const layer = computed(() => {
  const l = layersStore.getOrgLayerById(orgId.value, layerId.value)
  return l
})

const layersLoaded = computed(() => layersStore.hasOrgLayers(orgId.value))

const isPlanetscopeLayer = computed(() => {
  return layer.value?.properties?.type === 0
})

const isPlanetOrderLayer = computed(() => {
  return layer.value?.properties?.type === 1
})

const isPlanetVarsLayer = computed(() => {
  return layer.value?.properties?.type === 2
})

const isRadarLayer = computed(() => {
  return layer.value?.properties?.type === 3
})

const isCollectedDataLayer = computed(() => {
  return layer.value?.properties?.type === 4
})

const isStaticLayer = computed(() => {
  return layer.value?.properties?.type === 5
})

const isTaskingLayer = computed(() => {
  return layer.value?.properties?.type === 6
})

const isLulcLayer = computed(() => {
  return layer.value?.properties?.type === 7
})

const isLayerWithFeatures = computed(() => {
  return isCollectedDataLayer.value || isStaticLayer.value
})

const layerCode = computed(() => {
  return layer.value?.uuid
})

const settings = computed(() => userStore.settings)

const onOpacityChange = (value) => {
  opacity.value = value[0]

  if (!map.value || !layerId.value) return

  const groupId = `layer-${layerId.value}`

  try {
    const layers = map.value.getStyle().layers.filter((layer) =>
      layer.metadata?.groupId === groupId
    )

    layers.forEach((layer) => {
      if (layer.metadata?.opacityIgnore) return

      if (layer.type === 'fill') {
        map.value.setPaintProperty(
          layer.id,
          'fill-opacity',
          opacity.value / 100
        )
      } else if (layer.type === 'line') {
        map.value.setPaintProperty(
          layer.id,
          'line-opacity',
          opacity.value / 100
        )
      } else if (layer.type === 'circle') {
        map.value.setPaintProperty(
          layer.id,
          'circle-opacity',
          opacity.value / 100
        )
      } else if (layer.type === 'symbol') {
        map.value.setPaintProperty(
          layer.id,
          'text-opacity',
          opacity.value / 100
        )
      } else if (layer.type === 'raster') {
        map.value.setPaintProperty(
          layer.id,
          'raster-opacity',
          opacity.value / 100
        )
      }
    })

    const updatedLayer = {
      ...layer.value,
      properties: {
        ...layer.value.properties,
        // Preserve existing features data when updating opacity
        ...(layer.value?.properties?.features ? {
          features: layer.value.properties.features,
          featureCollection: layer.value.properties.featureCollection
        } : {}),
        opacity: opacity.value / 100
      }
    }

    layersStore.updateOrgLayer({
      orgId: orgId.value,
      layer: updatedLayer
    })
  } catch (error) {
    console.error('Error updating layer opacity:', error)
    toast({
      title: 'Update failed',
      description: 'Failed to update layer opacity',
      variant: 'destructive'
    })
  }
}

const ensureLayerVisible = () => {
  if (!layer.value || layer.value.properties?.visible) return

  // Update map layer visibility
  if (map.value) {
    const groupId = `layer-${layerId.value}`
    try {
      const mapLayers = map.value.getStyle().layers.filter((l) =>
        l.metadata?.groupId === groupId
      )
      mapLayers.forEach((l) => {
        map.value.setLayoutProperty(l.id, 'visibility', 'visible')
      })
    } catch (e) {
      // Map layers may not exist yet, visibility will be applied when they render
    }
  }

  // Update the layers store
  const updatedLayer = {
    ...layer.value,
    properties: {
      ...layer.value.properties,
      visible: true
    }
  }
  layersStore.updateOrgLayer({
    orgId: orgId.value,
    layer: updatedLayer
  })
}

const initLayer = () => {
  if (layer.value?.properties?.opacity !== undefined) {
    opacity.value = Math.round(layer.value.properties.opacity * 100)
  }
  ensureLayerVisible()
}

watch(layer, (newLayer, oldLayer) => {
  if (newLayer && !oldLayer) initLayer()
}, { immediate: true })

onUnmounted(() => {
  // Clear dates when leaving the layer details page to prevent data persistence between organizations
  layersStore.clearDates()
})
</script>

<template>
  <div v-if="layer">
    <div class="p-3">
      <div class="flex justify-between items-center">
        <h3 class="text-lg font-bold">{{ layer.properties.name || 'Unnamed' }}</h3>
      </div>
      <ul v-if="!isLulcLayer" class="text-gray-500">
        <li><span>Created at: </span> {{ formatTime(layer?.properties?.created_at, settings.tz, 'D MMMM YYYY HH:mm') }}</li>
        <li v-if="layer?.properties?.updated_at !== layer?.properties?.created_at">Updated at: {{ formatTime(layer?.properties?.updated_at, settings.tz, 'D MMMM YYYY HH:mm') }}</li>
      </ul>

      <!-- Layer Settings -->
      <div class="mt-4 pb-4 border-b">
        <div class="flex items-center gap-3">
          <label class="text-sm text-gray-600 w-16">Opacity</label>
          <Slider
            :model-value="[opacity]"
            :max="100"
            :step="1"
            class="flex-1"
            @update:model-value="onOpacityChange"
          />
          <span class="text-sm text-gray-500 w-10 text-right">{{ opacity }}%</span>
        </div>
      </div>
    </div>

    <!-- Project Data Layer (type 4) — full width for feed items -->
    <DetailCollectedData
      v-if="isCollectedDataLayer"
      :layer="layer"
      :activeTab="'data'"
    />

    <div v-else class="px-3">
      <!-- PlanetScope Layer (type 0) -->
      <DetailPlanetscope
        v-if="isPlanetscopeLayer"
        :layer="layer"
      />

      <!-- Planet Order Layer (type 1) -->
      <DetailPlanetOrder
        v-else-if="isPlanetOrderLayer"
        :layer="layer"
        :activeTab="'data'"
      />

      <!-- Planet Vars / Forest Carbon Layer (type 2) -->
      <DetailPlanetVars
        v-else-if="isPlanetVarsLayer"
        :layer="layer"
      />

      <!-- Radar Layer (type 3) -->
      <DetailRadar
        v-else-if="isRadarLayer"
        :layer="layer"
      />

      <!-- Static Layer (type 5) -->
      <DetailStaticLayer
        v-else-if="isStaticLayer"
        :layer="layer"
      />

      <!-- Tasking Layer (type 6) -->
      <DetailTasking
        v-else-if="isTaskingLayer"
        :layer="layer"
        :activeTab="'data'"
      />

      <!-- LULC Layer (type 7) -->
      <DetailLulc
        v-else-if="isLulcLayer"
        :layer="layer"
      />

      <!-- Other layer types -->
      <p v-else class="text-sm text-muted-foreground">No additional data available for this layer</p>
    </div>
  </div>

  <div v-else-if="!layersLoaded" class="p-3 text-sm text-muted-foreground">Loading layer...</div>

  <div v-else class="p-3 text-sm text-muted-foreground">Layer not found</div>
</template>
